// Librairies
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, DoCheck, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, ViewContainerRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';

// Interfaces
import { WidgetV2 } from 'src/app/interfaces/shared/widget';

// Classes
import { SHARED_WIDGET_INPUTS, WIDGET_REGISTRY, WidgetInputs, WidgetRegistration } from '../widget-registry';
import { WidgetDirective } from '../widget.directive';

// Renders the widget of WIDGET_REGISTRY matching widget.type, next to this element (it takes no box itself).
@Component({
  selector: 'app-widget-host',
  standalone: true,
  imports: [
    MatIconModule
  ],
  templateUrl: './widget-host.component.html',
  styleUrl: './widget-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class WidgetHostComponent implements OnChanges, DoCheck, OnDestroy {
  // Services
  private readonly container: ViewContainerRef = inject(ViewContainerRef);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);

  // Variables
  @Input({ required: true }) widget!: WidgetV2;
  @Input() inputs: WidgetInputs = {};
  @Output() exported: EventEmitter<any> = new EventEmitter();
  @Output() query: EventEmitter<any> = new EventEmitter();

  registration: WidgetRegistration | undefined;
  private ref: ComponentRef<WidgetDirective> | null = null;
  private type: string | undefined;
  private outputs: Subscription | null = null;

  // The created widget, e.g. for the chart zoom buttons of the dashboard.
  get instance(): WidgetDirective | null {
    return this.ref?.instance ?? null;
  }

  ngOnChanges(): void {
    this.render();
  }

  // The editor changes widget.type in place: follow it like the former ngSwitch did.
  ngDoCheck(): void {
    if (this.widget?.type !== this.type) this.render();
  }

  ngOnDestroy(): void {
    this.clear();
  }

  private render(): void {
    if (this.widget?.type !== this.type) this.create();
    if (!this.ref || !this.registration) return;

    const allowed = [...SHARED_WIDGET_INPUTS, ...(this.registration.extraInputs ?? [])];
    for (const key of allowed)
      if (key in this.inputs) this.ref.setInput(key, this.inputs[key]);
    this.ref.setInput('widget', this.widget);
  }

  private create(): void {
    this.clear();
    this.type = this.widget?.type;
    this.registration = this.type ? WIDGET_REGISTRY[this.type] : undefined;
    this.changeDetector.markForCheck();
    if (!this.registration) return;

    this.ref = this.container.createComponent(this.registration.component);
    this.outputs = this.ref.instance.onExport.subscribe(value => this.exported.emit(value));
    if (this.registration.emitsQuery)
      this.outputs.add(this.ref.instance.query.subscribe(value => this.query.emit(value)));
  }

  private clear(): void {
    this.outputs?.unsubscribe();
    this.outputs = null;
    this.ref?.destroy();
    this.ref = null;
  }
}
